import { Skeleton } from '@/components/ui/skeleton'
import {
  TableCell,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader } from '@/components/ui/card'

// ──────────────────────────────────────────────
// Table skeleton for list pages (Forms, Team, GlobalData)
// ──────────────────────────────────────────────
interface TableSkeletonProps {
  columns: number
  rows?: number
  hasCheckbox?: boolean
}

export function TableSkeleton({ columns, rows = 5, hasCheckbox = false }: TableSkeletonProps) {
  return (
    <>
      { Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={ rowIndex }>
          { hasCheckbox && (
            <TableCell className="w-[50px]">
              <Skeleton className="h-4 w-4" />
            </TableCell>
          ) }
          { Array.from({ length: columns }).map((_, colIndex) => (
            <TableCell key={ colIndex }>
              { colIndex === 0 ? (
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-[140px]" />
                  <Skeleton className="h-3 w-[100px]" />
                </div>
              ) : (
                <div className="flex justify-center">
                  <Skeleton className="h-4 w-[60px]" />
                </div>
              ) }
            </TableCell>
          )) }
        </TableRow>
      )) }
    </>
  )
}

// ──────────────────────────────────────────────
// Generic list page skeleton (neutral initial load)
// Used to avoid skeleton→empty state flash on pages
// that may or may not have data yet
// ──────────────────────────────────────────────
export function ListPageSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0 animate-in fade-in duration-300">
      {/* Header */ }
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-[180px]" />
        <Skeleton className="h-9 w-[100px] rounded-md" />
      </div>

      {/* Content Card */ }
      <div className="rounded-lg border bg-card">
        {/* Toolbar area */ }
        <div className="p-4 border-b">
          <Skeleton className="h-9 w-full max-w-sm rounded-md" />
        </div>

        {/* Content rows */ }
        <div className="p-4 space-y-4">
          { Array.from({ length: 5 }).map((_, i) => (
            <div key={ i } className="flex items-center gap-4">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-3 w-[140px]" />
              </div>
              <Skeleton className="h-4 w-[60px]" />
              <Skeleton className="h-4 w-[60px]" />
              <Skeleton className="h-6 w-[60px] rounded-full" />
            </div>
          )) }
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Dashboard skeleton
// ──────────────────────────────────────────────
export function DashboardSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0 animate-in fade-in duration-300">
      {/* Header */ }
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-[200px]" />
      </div>

      {/* Stats Cards */ }
      <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
        { Array.from({ length: 4 }).map((_, i) => (
          <Card key={ i } className="py-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-7 w-[60px]" />
                  <Skeleton className="h-4 w-[80px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        )) }
      </div>

      {/* Chart */ }
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-[160px]" />
            <Skeleton className="h-9 w-[180px] rounded-md" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Skeleton className="h-[280px] w-full rounded-lg" />
        </CardContent>
      </Card>

      {/* Bottom Grid */ }
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        { Array.from({ length: 2 }).map((_, i) => (
          <Card key={ i }>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-[180px]" />
                <Skeleton className="h-8 w-[70px] rounded-md" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                { Array.from({ length: 4 }).map((_, j) => (
                  <div key={ j } className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-md" />
                      <Skeleton className="h-4 w-[120px]" />
                    </div>
                    <Skeleton className="h-5 w-[50px] rounded-full" />
                  </div>
                )) }
              </div>
            </CardContent>
          </Card>
        )) }
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Detail page skeleton (Lead, Submission, Appointment)
// ──────────────────────────────────────────────
interface DetailPageSkeletonProps {
  layout?: 'two-column' | 'three-column'
}

export function DetailPageSkeleton({ layout = 'two-column' }: DetailPageSkeletonProps) {
  const gridCols = layout === 'three-column'
    ? 'grid-cols-1 lg:grid-cols-3'
    : 'grid-cols-1 lg:grid-cols-5'

  const leftCols = layout === 'three-column' ? 'lg:col-span-2' : 'lg:col-span-3'
  const rightCols = layout === 'three-column' ? '' : 'lg:col-span-2'

  return (
    <div className="w-full px-4 sm:px-0 space-y-6 animate-in fade-in duration-300">
      {/* Back Button */ }
      <Skeleton className="h-9 w-[160px] rounded-md" />

      {/* Header Card */ }
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-[200px]" />
                <Skeleton className="h-4 w-[150px]" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-[80px] rounded-full" />
              <Skeleton className="h-6 w-[50px] rounded-full" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Grid */ }
      <div className={ `grid ${gridCols} gap-6` }>
        {/* Left Column */ }
        <div className={ `${leftCols} space-y-6` }>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-[140px]" />
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              { Array.from({ length: 4 }).map((_, i) => (
                <div key={ i } className="space-y-1.5">
                  <Skeleton className="h-3 w-[80px]" />
                  <Skeleton className="h-4 w-full max-w-[250px]" />
                </div>
              )) }
            </CardContent>
          </Card>
        </div>

        {/* Right Column */ }
        <div className={ `${rightCols} space-y-6` }>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-[160px]" />
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-[120px]" />
                <Skeleton className="h-3 w-[160px]" />
              </div>
              <Skeleton className="h-9 w-full rounded-md" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-[120px]" />
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              { Array.from({ length: 3 }).map((_, i) => (
                <div key={ i } className="flex justify-between items-center">
                  <Skeleton className="h-4 w-[100px]" />
                  <Skeleton className="h-4 w-[80px]" />
                </div>
              )) }
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Lead detail page skeleton
// ──────────────────────────────────────────────
export function LeadDetailSkeleton() {
  return (
    <div className="w-full px-4 sm:px-0 animate-in fade-in duration-300">
      {/* Back Button */ }
      <div className="mb-6">
        <Skeleton className="h-9 w-[130px] rounded-md" />
      </div>

      {/* Content Grid */ }
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        {/* Left - Submissions */ }
        <Card>
          <CardHeader className="border-b bg-muted/30 py-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-[160px]" />
              <Skeleton className="h-6 w-[30px] rounded-full" />
            </div>
          </CardHeader>
          <CardContent className="p-3 space-y-4">
            { Array.from({ length: 2 }).map((_, i) => (
              <div key={ i } className="rounded-lg border p-4 space-y-2">
                <Skeleton className="h-5 w-[180px]" />
                <Skeleton className="h-3 w-[120px]" />
              </div>
            )) }
          </CardContent>
        </Card>

        {/* Right - Profile, Timeline & Info */ }
        <div className="space-y-4">
          {/* Profile Card */ }
          <Card>
            <CardContent className="p-5">
              <div className="mb-4">
                <div className="space-y-2">
                  <Skeleton className="h-6 w-[140px]" />
                  <Skeleton className="h-3 w-[100px]" />
                </div>
              </div>
              <div className="space-y-2 pt-3 border-t">
                <Skeleton className="h-4 w-[180px]" />
                <Skeleton className="h-4 w-[130px]" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <Skeleton className="h-5 w-[140px]" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                { Array.from({ length: 4 }).map((_, i) => (
                  <div key={ i } className="space-y-1.5">
                    <Skeleton className="h-4 w-full max-w-[200px]" />
                    <Skeleton className="h-3 w-[100px]" />
                  </div>
                )) }
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-4">
              <Skeleton className="h-5 w-[80px]" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                { Array.from({ length: 4 }).map((_, i) => (
                  <div key={ i } className="flex items-center justify-between pb-3 border-b last:border-0">
                    <Skeleton className="h-4 w-[80px]" />
                    <Skeleton className="h-4 w-[100px]" />
                  </div>
                )) }
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Split test details skeleton
// ──────────────────────────────────────────────
export function SplitTestDetailSkeleton() {
  return (
    <div className="w-full px-4 sm:px-0 space-y-6 animate-in fade-in duration-300">
      {/* Back + Header */ }
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <div className="space-y-1.5">
            <Skeleton className="h-7 w-[250px]" />
            <Skeleton className="h-4 w-[180px]" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-[70px] rounded-full" />
          <Skeleton className="h-9 w-[80px] rounded-md" />
        </div>
      </div>

      {/* Variants Comparison */ }
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        { Array.from({ length: 2 }).map((_, i) => (
          <Card key={ i }>
            <CardContent className="p-6">
              <div className="mb-4 space-y-1.5">
                <Skeleton className="h-5 w-[120px]" />
                <Skeleton className="h-3 w-[80px]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                { Array.from({ length: 4 }).map((_, j) => (
                  <div key={ j } className="space-y-1">
                    <Skeleton className="h-3 w-[60px]" />
                    <Skeleton className="h-6 w-[80px]" />
                  </div>
                )) }
              </div>
            </CardContent>
          </Card>
        )) }
      </div>

      {/* Stats Table */ }
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[180px]" />
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            { Array.from({ length: 5 }).map((_, i) => (
              <div key={ i } className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <Skeleton className="h-4 w-[120px]" />
                <div className="flex gap-8">
                  <Skeleton className="h-4 w-[60px]" />
                  <Skeleton className="h-4 w-[60px]" />
                </div>
              </div>
            )) }
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ──────────────────────────────────────────────
// Split test list skeleton
// ──────────────────────────────────────────────
export function SplitTestListSkeleton() {
  return (
    <div className="space-y-3">
      { Array.from({ length: 3 }).map((_, i) => (
        <div key={ i } className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-6">
            <div className="flex-1 min-w-0 space-y-2">
              <Skeleton className="h-5 w-[60px] rounded-full" />
              <Skeleton className="h-5 w-[200px]" />
              <div className="flex items-center gap-3">
                <Skeleton className="h-3 w-[60px]" />
                <Skeleton className="h-3 w-[80px]" />
                <Skeleton className="h-3 w-[40px]" />
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <div className="text-center space-y-1.5">
                <Skeleton className="h-3 w-[50px]" />
                <Skeleton className="h-6 w-[50px] mx-auto" />
              </div>
              <Skeleton className="h-4 w-[20px]" />
              <div className="text-center space-y-1.5">
                <Skeleton className="h-3 w-[50px]" />
                <Skeleton className="h-6 w-[50px] mx-auto" />
              </div>
            </div>
            <Skeleton className="h-8 w-[80px] rounded-md" />
          </div>
        </div>
      )) }
    </div>
  )
}

// ──────────────────────────────────────────────
// Form analytics skeleton
// ──────────────────────────────────────────────
export function FormAnalyticsSkeleton() {
  return (
    <div className="space-y-6 pb-8 animate-in fade-in duration-300">
      {/* Header */ }
      <div className="flex items-center gap-4 p-6 bg-card rounded-lg border">
        <Skeleton className="h-6 w-6 rounded" />
        <div className="space-y-1.5">
          <Skeleton className="h-6 w-[100px]" />
          <Skeleton className="h-4 w-[260px]" />
        </div>
      </div>

      {/* Key Rates */ }
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        { Array.from({ length: 4 }).map((_, i) => (
          <div key={ i } className="flex items-center gap-3 p-4 bg-card rounded-lg border">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-6 w-[50px]" />
              <Skeleton className="h-3 w-[70px]" />
            </div>
          </div>
        )) }
      </div>

      {/* Counts */ }
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        { Array.from({ length: 5 }).map((_, i) => (
          <div key={ i } className="flex items-center gap-2 p-3 bg-card rounded-lg border">
            <Skeleton className="h-4 w-4 rounded" />
            <div className="flex flex-col gap-1">
              <Skeleton className="h-5 w-[40px]" />
              <Skeleton className="h-3 w-[60px]" />
            </div>
          </div>
        )) }
      </div>

      {/* Drop-off Funnel */ }
      <div className="bg-card rounded-lg border overflow-hidden">
        <div className="flex items-center gap-2 p-4 border-b bg-muted/30">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-5 w-[120px]" />
        </div>
        <div className="p-4 space-y-3">
          { Array.from({ length: 4 }).map((_, i) => (
            <div key={ i } className="space-y-1">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-[160px]" />
                <Skeleton className="h-4 w-[40px]" />
              </div>
              <Skeleton className="h-6 w-full rounded" />
            </div>
          )) }
        </div>
      </div>

      {/* Question Insights */ }
      <div className="space-y-3">
        <Skeleton className="h-4 w-[140px]" />
        { Array.from({ length: 3 }).map((_, i) => (
          <div key={ i } className="bg-card rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-7 w-7 rounded-md" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-3 w-[140px]" />
              </div>
            </div>
          </div>
        )) }
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Integration list skeleton
// ──────────────────────────────────────────────
export function IntegrationListSkeleton() {
  return (
    <div className="divide-y">
      { Array.from({ length: 5 }).map((_, i) => (
        <div key={ i } className="p-4">
          <div className="flex items-center gap-4">
            <Skeleton className="w-12 h-12 rounded-lg" />
            <div className="flex-1 min-w-0 space-y-1.5">
              <Skeleton className="h-5 w-[130px]" />
              <Skeleton className="h-4 w-[260px]" />
            </div>
            <Skeleton className="h-9 w-[90px] rounded-md" />
          </div>
        </div>
      )) }
    </div>
  )
}

// ──────────────────────────────────────────────
// Form builder skeleton (full-screen 3-panel layout)
// ──────────────────────────────────────────────
export function FormBuilderSkeleton() {
  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Header */ }
      <div className="flex items-center justify-between px-4 py-3 bg-card border-b gap-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-7 w-[200px]" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-[280px] rounded-lg" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-[75px] rounded-md" />
          <Skeleton className="h-9 w-[70px] rounded-md" />
          <Skeleton className="h-9 w-[85px] rounded-md" />
        </div>
      </div>

      {/* Main Content */ }
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Elements */ }
        <div className="w-64 border-r bg-card flex flex-col shrink-0">
          <div className="p-3 border-b">
            <Skeleton className="h-9 w-full rounded-md" />
          </div>
          <div className="flex-1 p-2 space-y-1">
            { Array.from({ length: 5 }).map((_, i) => (
              <div key={ i } className="flex items-center gap-2 px-3 py-2.5 rounded-lg">
                <Skeleton className="h-7 w-7 rounded-md" />
                <Skeleton className="h-4 flex-1" />
              </div>
            )) }
          </div>
          <div className="border-t p-3">
            <Skeleton className="h-4 w-[60px] mb-2" />
            <div className="space-y-1">
              { Array.from({ length: 2 }).map((_, i) => (
                <div key={ i } className="flex items-center gap-2 px-3 py-2 rounded-lg">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-[80px]" />
                </div>
              )) }
            </div>
          </div>
        </div>

        {/* Center - Canvas */ }
        <div className="flex-1 flex items-center justify-center bg-muted/20">
          <div className="w-full max-w-[640px] bg-card rounded-xl border shadow-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <Skeleton className="h-8 w-[300px] mx-auto" />
              <Skeleton className="h-4 w-[200px] mx-auto" />
            </div>
            { Array.from({ length: 3 }).map((_, i) => (
              <div key={ i } className="space-y-2">
                <Skeleton className="h-4 w-[160px]" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            )) }
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>

        {/* Right Panel - Settings */ }
        <div className="w-80 border-l bg-card flex flex-col shrink-0">
          <div className="p-4 border-b">
            <Skeleton className="h-5 w-[140px]" />
          </div>
          <div className="p-4 space-y-5">
            { Array.from({ length: 4 }).map((_, i) => (
              <div key={ i } className="space-y-2">
                <Skeleton className="h-4 w-[80px]" />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            )) }
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-[80px]" />
              <Skeleton className="h-5 w-9 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Calendar widget skeleton (for preview & booking)
// ──────────────────────────────────────────────
interface CalendarWidgetSkeletonProps {
  variant?: 'dark' | 'light'
}

export function CalendarWidgetSkeleton({ variant = 'dark' }: CalendarWidgetSkeletonProps) {
  const bg = variant === 'dark' ? 'bg-white/10' : 'bg-accent'

  return (
    <div className="w-full max-w-[800px]">
      <div
        className="relative rounded-xl overflow-hidden shadow-lg"
        style={ {
          backgroundColor: variant === 'dark' ? '#1e293b' : undefined,
        } }
      >
        {/* Calendar section */ }
        <div className="p-4 sm:p-6 sm:pr-[240px]">
          {/* Header */ }
          <div className="flex items-center justify-between mb-4">
            <Skeleton className={ `h-8 w-8 rounded-md ${bg}` } />
            <Skeleton className={ `h-6 w-[160px] ${bg}` } />
            <Skeleton className={ `h-8 w-8 rounded-md ${bg}` } />
          </div>

          {/* Day labels */ }
          <div className="grid grid-cols-7 gap-1 mb-1">
            { ['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((_, i) => (
              <Skeleton key={ i } className={ `h-4 w-full ${bg}` } />
            )) }
          </div>

          {/* Calendar grid */ }
          <div className="grid grid-cols-7 gap-1">
            { Array.from({ length: 35 }).map((_, i) => (
              <Skeleton key={ i } className={ `aspect-square rounded-md ${bg}` } />
            )) }
          </div>
        </div>

        {/* Time panel */ }
        <div
          className="relative p-4 sm:p-5 border-t sm:absolute sm:right-0 sm:top-0 sm:bottom-0 sm:w-[220px] sm:border-t-0 sm:border-l flex flex-col items-center justify-center gap-3"
          style={ {
            borderColor: variant === 'dark' ? 'rgba(255,255,255,0.1)' : undefined,
            backgroundColor: variant === 'dark' ? '#334155' : undefined,
          } }
        >
          <div className="space-y-1.5 text-center">
            <Skeleton className={ `h-5 w-[100px] mx-auto ${bg}` } />
            <Skeleton className={ `h-4 w-[80px] mx-auto ${bg}` } />
          </div>
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// CRM field mapping skeleton (inline small panel)
// ──────────────────────────────────────────────
export function CrmFieldMappingSkeleton() {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-3.5 w-3.5 rounded" />
        <Skeleton className="h-3 w-[130px]" />
      </div>
      <div className="p-3 bg-muted/50 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-[100px]" />
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// Invitation verification card skeleton
// ──────────────────────────────────────────────
export function InvitationCardSkeleton() {
  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center space-y-3">
          <Skeleton className="h-6 w-[200px] mx-auto" />
          <Skeleton className="h-4 w-[260px] mx-auto" />
        </CardHeader>
        <CardContent className="flex justify-center pb-6">
          <Skeleton className="h-4 w-[180px]" />
        </CardContent>
      </Card>
    </div>
  )
}

// ──────────────────────────────────────────────
// Invoices table skeleton (Billing Settings)
// ──────────────────────────────────────────────
export function InvoicesTableSkeleton() {
  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Table Header */}
      <div className="flex items-center gap-4 px-4 py-3 bg-muted/30 border-b">
        <Skeleton className="h-4 w-[60px]" />
        <Skeleton className="h-4 w-[70px]" />
        <Skeleton className="h-4 w-[60px]" />
        <Skeleton className="h-4 w-[80px]" />
        <div className="flex-1" />
        <Skeleton className="h-4 w-[70px]" />
      </div>

      {/* Table Rows */}
      { Array.from({ length: 4 }).map((_, i) => (
        <div key={ i } className="flex items-center gap-4 px-4 py-3 border-b last:border-0">
          <Skeleton className="h-4 w-[90px]" />
          <Skeleton className="h-4 w-[60px]" />
          <Skeleton className="h-5 w-[50px] rounded-full" />
          <Skeleton className="h-4 w-[100px]" />
          <div className="flex-1" />
          <Skeleton className="h-8 w-[60px] rounded-md" />
        </div>
      )) }
    </div>
  )
}

// ──────────────────────────────────────────────
// Agency detail page skeleton
// ──────────────────────────────────────────────
export function AgencyDetailSkeleton() {
  return (
    <div className="w-full px-4 sm:px-0 space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Header Info */ }
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-8 w-[250px]" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-[80px]" />
            <Skeleton className="h-5 w-[40px] rounded-full" />
          </div>
          <Skeleton className="h-9 w-[140px] rounded-md" />
        </div>
      </div>

      {/* Tabs list */ }
      <div className="flex gap-2 border-b">
        <Skeleton className="h-10 w-[80px] rounded-t-md rounded-b-none" />
        <Skeleton className="h-10 w-[120px] rounded-t-md rounded-b-none" />
        <Skeleton className="h-10 w-[130px] rounded-t-md rounded-b-none" />
        <Skeleton className="h-10 w-[100px] rounded-t-md rounded-b-none" />
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="py-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-7 w-[60px]" />
                  <Skeleton className="h-4 w-[100px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Overview content */ }
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        { Array.from({ length: 3 }).map((_, i) => (
          <Card key={ i } className="py-0 gap-0 shadow-sm">
            <CardHeader className="px-5 py-4 gap-0 border-b">
              <Skeleton className="h-5 w-[140px] mb-1" />
              <Skeleton className="h-4 w-[180px]" />
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              { Array.from({ length: 5 }).map((_, j) => (
                <div key={ j } className="flex justify-between items-center gap-4">
                  <Skeleton className="h-4 w-[100px]" />
                  <Skeleton className="h-4 w-[120px]" />
                </div>
              )) }
            </CardContent>
          </Card>
        )) }
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────
// X-Ray settings page skeleton (matches XraySettings.tsx layout)
// ──────────────────────────────────────────────
export function XraySettingsSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0 animate-in fade-in duration-300">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-[220px]" />
        <Skeleton className="h-4 w-[420px]" />
      </div>

      {/* Pricing grid card */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[220px] mb-1" />
          <Skeleton className="h-4 w-[440px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-4 border-b pb-3 last:border-0">
                <Skeleton className="h-4 w-[160px]" />
                <Skeleton className="h-9 w-[160px] rounded-md" />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Skeleton className="h-9 w-[160px] rounded-md" />
          </div>
        </CardContent>
      </Card>

      {/* Plan cards */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[140px] mb-1" />
          <Skeleton className="h-4 w-[480px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="grid grid-cols-6 gap-3 items-center border-b pb-3 last:border-0">
                <Skeleton className="h-9" />
                <Skeleton className="h-9" />
                <Skeleton className="h-9" />
                <Skeleton className="h-9" />
                <Skeleton className="h-9 col-span-1" />
                <Skeleton className="h-9 w-[80px] rounded-md ml-auto" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  )
}

// ──────────────────────────────────────────────
// X-Ray agency panel skeleton (Agency Detail → X-Ray tab)
// Matches AgencyXrayPanel layout: 2-col row (wallet + plan) over a full-width
// Add funds card.
// ──────────────────────────────────────────────
export function AgencyXrayPanelSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Wallet */}
        <Card className="py-0">
          <CardContent className="p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-5 w-[120px]" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-[120px]" />
                <Skeleton className="h-3 w-[60px]" />
              </div>
              <Skeleton className="h-1.5 w-full rounded-full" />
              <div className="flex gap-3 mt-1">
                <Skeleton className="h-2.5 w-[60px]" />
                <Skeleton className="h-2.5 w-[60px]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plan & pricing */}
        <Card className="py-0">
          <CardContent className="p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-5 w-[140px]" />
            </div>
            <div className="flex flex-col gap-2.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <Skeleton className="h-3 w-[80px]" />
                  <Skeleton className="h-5 w-[140px] rounded-md" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add funds */}
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-[180px] mb-1" />
          <Skeleton className="h-4 w-[360px]" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-3 items-end">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9 w-[140px] rounded-md" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ──────────────────────────────────────────────
// Revenue dashboard skeleton
// ──────────────────────────────────────────────
export function RevenueSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0 animate-in fade-in duration-300">
      {/* Header */ }
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-9 w-[200px] rounded-md" />
      </div>

      {/* KPI Cards */ }
      <div className="grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-3 gap-4">
        { Array.from({ length: 6 }).map((_, i) => (
          <Card key={ i } className="py-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-7 w-[80px]" />
                  <Skeleton className="h-4 w-[100px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        )) }
      </div>

      {/* Plan Distribution + MRR Over Time, each full width */ }
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-6 w-[180px]" />
          </CardHeader>
          <CardContent className="pt-0">
            <Skeleton className="h-[280px] w-full rounded-lg" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-6 w-[180px]" />
          </CardHeader>
          <CardContent className="pt-0">
            <Skeleton className="h-[280px] w-full rounded-lg" />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */ }
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        { Array.from({ length: 2 }).map((_, i) => (
          <Card key={ i }>
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-[180px]" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                { Array.from({ length: 4 }).map((_, j) => (
                  <div key={ j } className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <Skeleton className="h-4 w-[140px]" />
                    <Skeleton className="h-5 w-[50px] rounded-full" />
                  </div>
                )) }
              </div>
            </CardContent>
          </Card>
        )) }
      </div>

      {/* Affiliate Table */ }
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-[180px]" />
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            { Array.from({ length: 3 }).map((_, i) => (
              <div key={ i } className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <Skeleton className="h-4 w-[200px]" />
                <div className="flex gap-6">
                  <Skeleton className="h-4 w-[60px]" />
                  <Skeleton className="h-4 w-[60px]" />
                  <Skeleton className="h-4 w-[60px]" />
                </div>
              </div>
            )) }
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
